import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Tooltip,
  Chip,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Settings as SettingsIcon,
  Message as SmsIcon,
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  VideoCall as VideoIcon,
} from '@mui/icons-material';
import TwilioSettingsService, { TwilioSettings, ServiceStatus, UsageStats } from '../../services/TwilioSettingsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`twilio-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TwilioSettingsComponent: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [settings, setSettings] = useState<TwilioSettings>({
    account_sid: '',
    auth_token: '',
    is_active: false,
    sms_enabled: false,
    sms_phone_number: '',
    sms_webhook_url: '',
    sms_status_callback_url: '',
    whatsapp_enabled: false,
    whatsapp_phone_number: '',
    whatsapp_webhook_url: '',
    whatsapp_status_callback_url: '',
    voice_enabled: false,
    voice_phone_number: '',
    voice_webhook_url: '',
    voice_status_callback_url: '',
    voice_recording_enabled: false,
    voice_recording_webhook_url: '',
    email_enabled: false,
    sendgrid_api_key: '',
    email_from_address: '',
    email_from_name: '',
    email_domain: '',
    email_inbound_webhook_url: '',
    email_tracking_enabled: true,
    video_enabled: false,
    video_api_key: '',
    video_api_secret: '',
    video_max_participants: 10,
    video_recording_enabled: false,
    webhook_secret: '',
    webhook_signature_validation: true,
  });

  const [status, setStatus] = useState<ServiceStatus | null>(null);
  const [usage, setUsage] = useState<UsageStats | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [settingsData, statusData, usageData] = await Promise.all([
        TwilioSettingsService.getSettings().catch(err => {
          console.error('Error loading settings:', err);
          return settings; // Return current settings if API fails
        }),
        TwilioSettingsService.getStatus().catch(err => {
          console.error('Error loading status:', err);
          return null; // Return null if API fails
        }),
        TwilioSettingsService.getUsage().catch(err => {
          console.error('Error loading usage:', err);
          return null; // Return null if API fails
        }),
      ]);

      if (settingsData) setSettings(settingsData);
      if (statusData) setStatus(statusData);
      if (usageData) setUsage(usageData);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to load Twilio settings';
      setError(errorMessage);
      console.error('Error loading Twilio settings:', err);
      
      // Show more helpful error for auth issues
      if (err.response?.status === 401) {
        setError('Authentication required. Please ensure you are logged in as an administrator.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await TwilioSettingsService.updateSettings(settings);
      
      setSuccess('Twilio settings updated successfully');
      await loadData(); // Reload to get updated status
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save settings');
      console.error('Error saving settings:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async (service: string) => {
    try {
      setError(null);
      const result = await TwilioSettingsService.testConnection(
        settings.account_sid,
        settings.auth_token,
        service
      );
      
      if (result.success) {
        setSuccess(`${service.toUpperCase()} connection test successful`);
      } else {
        setError(`${service.toUpperCase()} connection test failed`);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Connection test failed');
    }
  };

  const handleResetUsage = async () => {
    if (!window.confirm('Are you sure you want to reset usage statistics?')) {
      return;
    }

    try {
      await TwilioSettingsService.resetUsage();
      setSuccess('Usage statistics reset successfully');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset usage');
    }
  };

  const getUsagePercentage = (service: string): number => {
    if (!usage || !usage.current_usage || !usage.limits) {
      return 0;
    }
    const current = usage.current_usage[service] || 0;
    const limit = usage.limits[service] || 0;
    return limit > 0 ? (current / limit) * 100 : 0;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          <SettingsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Twilio Configuration
        </Typography>
        <Box>
          <Tooltip title="Refresh">
            <IconButton onClick={loadData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            sx={{ ml: 2 }}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Service Status Overview */}
      {status?.services && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Service Status</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2.4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <SmsIcon sx={{ fontSize: 40, color: status.services?.sms?.ready ? 'success.main' : 'text.disabled' }} />
                  <Typography variant="body2">SMS</Typography>
                  <Chip 
                    label={status.services?.sms?.ready ? 'Ready' : 'Not Ready'} 
                    color={status.services?.sms?.ready ? 'success' : 'default'}
                    size="small"
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <WhatsAppIcon sx={{ fontSize: 40, color: status.services?.whatsapp?.ready ? 'success.main' : 'text.disabled' }} />
                  <Typography variant="body2">WhatsApp</Typography>
                  <Chip 
                    label={status.services?.whatsapp?.ready ? 'Ready' : 'Not Ready'} 
                    color={status.services?.whatsapp?.ready ? 'success' : 'default'}
                    size="small"
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <PhoneIcon sx={{ fontSize: 40, color: status.services?.voice?.ready ? 'success.main' : 'text.disabled' }} />
                  <Typography variant="body2">Voice</Typography>
                  <Chip 
                    label={status.services?.voice?.ready ? 'Ready' : 'Not Ready'} 
                    color={status.services?.voice?.ready ? 'success' : 'default'}
                    size="small"
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <EmailIcon sx={{ fontSize: 40, color: status.services?.email?.ready ? 'success.main' : 'text.disabled' }} />
                  <Typography variant="body2">Email</Typography>
                  <Chip 
                    label={status.services?.email?.ready ? 'Ready' : 'Not Ready'} 
                    color={status.services?.email?.ready ? 'success' : 'default'}
                    size="small"
                  />
                </Paper>
              </Grid>
              <Grid item xs={12} md={2.4}>
                <Paper sx={{ p: 2, textAlign: 'center' }}>
                  <VideoIcon sx={{ fontSize: 40, color: status.services?.video?.ready ? 'success.main' : 'text.disabled' }} />
                  <Typography variant="body2">Video</Typography>
                  <Chip 
                    label={status.services?.video?.ready ? 'Ready' : 'Not Ready'} 
                    color={status.services?.video?.ready ? 'success' : 'default'}
                    size="small"
                  />
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="General" />
        <Tab label="SMS" />
        <Tab label="WhatsApp" />
        <Tab label="Voice" />
        <Tab label="Email" />
        <Tab label="Video" />
        <Tab label="Usage" />
      </Tabs>

      {/* General Settings */}
      <TabPanel value={activeTab} index={0}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Twilio Account Credentials</Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.is_active}
                      onChange={(e) => setSettings({ ...settings, is_active: e.target.checked })}
                    />
                  }
                  label="Enable Twilio Integration"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Account SID"
                  value={settings.account_sid}
                  onChange={(e) => setSettings({ ...settings, account_sid: e.target.value })}
                  helperText="Your Twilio Account SID"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Auth Token"
                  value={settings.auth_token}
                  onChange={(e) => setSettings({ ...settings, auth_token: e.target.value })}
                  helperText="Your Twilio Auth Token (encrypted)"
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>Webhook Security</Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Webhook Secret"
                  value={settings.webhook_secret}
                  onChange={(e) => setSettings({ ...settings, webhook_secret: e.target.value })}
                  helperText="Secret for webhook signature validation"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.webhook_signature_validation}
                      onChange={(e) => setSettings({ ...settings, webhook_signature_validation: e.target.checked })}
                    />
                  }
                  label="Enable Webhook Signature Validation"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* SMS Settings */}
      <TabPanel value={activeTab} index={1}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">SMS Configuration</Typography>
              <Button onClick={() => handleTestConnection('sms')} variant="outlined" size="small">
                Test Connection
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.sms_enabled}
                      onChange={(e) => setSettings({ ...settings, sms_enabled: e.target.checked })}
                    />
                  }
                  label="Enable SMS Service"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="SMS Phone Number"
                  value={settings.sms_phone_number}
                  onChange={(e) => setSettings({ ...settings, sms_phone_number: e.target.value })}
                  placeholder="+1234567890"
                  helperText="Your Twilio phone number for SMS"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Webhook URL"
                  value={settings.sms_webhook_url}
                  onChange={(e) => setSettings({ ...settings, sms_webhook_url: e.target.value })}
                  helperText="URL for incoming SMS messages"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Status Callback URL"
                  value={settings.sms_status_callback_url}
                  onChange={(e) => setSettings({ ...settings, sms_status_callback_url: e.target.value })}
                  helperText="URL for SMS delivery status updates"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* WhatsApp Settings */}
      <TabPanel value={activeTab} index={2}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">WhatsApp Configuration</Typography>
              <Button onClick={() => handleTestConnection('whatsapp')} variant="outlined" size="small">
                Test Connection
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.whatsapp_enabled}
                      onChange={(e) => setSettings({ ...settings, whatsapp_enabled: e.target.checked })}
                    />
                  }
                  label="Enable WhatsApp Service"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="WhatsApp Phone Number"
                  value={settings.whatsapp_phone_number}
                  onChange={(e) => setSettings({ ...settings, whatsapp_phone_number: e.target.value })}
                  placeholder="whatsapp:+1234567890"
                  helperText="Your Twilio WhatsApp number (include whatsapp: prefix)"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Webhook URL"
                  value={settings.whatsapp_webhook_url}
                  onChange={(e) => setSettings({ ...settings, whatsapp_webhook_url: e.target.value })}
                  helperText="URL for incoming WhatsApp messages"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Status Callback URL"
                  value={settings.whatsapp_status_callback_url}
                  onChange={(e) => setSettings({ ...settings, whatsapp_status_callback_url: e.target.value })}
                  helperText="URL for WhatsApp delivery status updates"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Voice Settings */}
      <TabPanel value={activeTab} index={3}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Voice Configuration</Typography>
              <Button onClick={() => handleTestConnection('voice')} variant="outlined" size="small">
                Test Connection
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.voice_enabled}
                      onChange={(e) => setSettings({ ...settings, voice_enabled: e.target.checked })}
                    />
                  }
                  label="Enable Voice Service"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Voice Phone Number"
                  value={settings.voice_phone_number}
                  onChange={(e) => setSettings({ ...settings, voice_phone_number: e.target.value })}
                  placeholder="+1234567890"
                  helperText="Your Twilio phone number for voice calls"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Webhook URL"
                  value={settings.voice_webhook_url}
                  onChange={(e) => setSettings({ ...settings, voice_webhook_url: e.target.value })}
                  helperText="URL for incoming voice calls"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Status Callback URL"
                  value={settings.voice_status_callback_url}
                  onChange={(e) => setSettings({ ...settings, voice_status_callback_url: e.target.value })}
                  helperText="URL for call status updates"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.voice_recording_enabled}
                      onChange={(e) => setSettings({ ...settings, voice_recording_enabled: e.target.checked })}
                    />
                  }
                  label="Enable Call Recording"
                />
              </Grid>

              {settings.voice_recording_enabled && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Recording Webhook URL"
                    value={settings.voice_recording_webhook_url}
                    onChange={(e) => setSettings({ ...settings, voice_recording_webhook_url: e.target.value })}
                    helperText="URL for recording status callbacks"
                  />
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Email Settings */}
      <TabPanel value={activeTab} index={4}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Email Configuration (SendGrid)</Typography>
              <Button onClick={() => handleTestConnection('email')} variant="outlined" size="small">
                Test Connection
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.email_enabled}
                      onChange={(e) => setSettings({ ...settings, email_enabled: e.target.checked })}
                    />
                  }
                  label="Enable Email Service"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="password"
                  label="SendGrid API Key"
                  value={settings.sendgrid_api_key}
                  onChange={(e) => setSettings({ ...settings, sendgrid_api_key: e.target.value })}
                  helperText="Your SendGrid API key (encrypted)"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="From Email Address"
                  type="email"
                  value={settings.email_from_address}
                  onChange={(e) => setSettings({ ...settings, email_from_address: e.target.value })}
                  placeholder="noreply@example.com"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="From Name"
                  value={settings.email_from_name}
                  onChange={(e) => setSettings({ ...settings, email_from_name: e.target.value })}
                  placeholder="VegaDigital"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Domain"
                  value={settings.email_domain}
                  onChange={(e) => setSettings({ ...settings, email_domain: e.target.value })}
                  placeholder="example.com"
                  helperText="Verified sending domain"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Inbound Webhook URL"
                  value={settings.email_inbound_webhook_url}
                  onChange={(e) => setSettings({ ...settings, email_inbound_webhook_url: e.target.value })}
                  helperText="URL for receiving inbound emails"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.email_tracking_enabled}
                      onChange={(e) => setSettings({ ...settings, email_tracking_enabled: e.target.checked })}
                    />
                  }
                  label="Enable Email Tracking (opens, clicks)"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Video Settings */}
      <TabPanel value={activeTab} index={5}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Video Configuration (Twilio Video)</Typography>
              <Button onClick={() => handleTestConnection('video')} variant="outlined" size="small">
                Test Connection
              </Button>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.video_enabled}
                      onChange={(e) => setSettings({ ...settings, video_enabled: e.target.checked })}
                    />
                  }
                  label="Enable Video Service"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Video API Key"
                  value={settings.video_api_key}
                  onChange={(e) => setSettings({ ...settings, video_api_key: e.target.value })}
                  helperText="Your Twilio Video API Key"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="password"
                  label="Video API Secret"
                  value={settings.video_api_secret}
                  onChange={(e) => setSettings({ ...settings, video_api_secret: e.target.value })}
                  helperText="Your Twilio Video API Secret (encrypted)"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Max Participants"
                  value={settings.video_max_participants}
                  onChange={(e) => setSettings({ ...settings, video_max_participants: parseInt(e.target.value) })}
                  inputProps={{ min: 2, max: 50 }}
                  helperText="Maximum participants per video room"
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.video_recording_enabled}
                      onChange={(e) => setSettings({ ...settings, video_recording_enabled: e.target.checked })}
                    />
                  }
                  label="Enable Video Recording"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Usage Statistics */}
      <TabPanel value={activeTab} index={6}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Usage Statistics</Typography>
              <Button onClick={handleResetUsage} variant="outlined" size="small" color="warning">
                Reset Usage
              </Button>
            </Box>

            {usage && (
              <Grid container spacing={3}>
                {['sms', 'whatsapp', 'voice', 'email', 'video'].map((service) => (
                  <Grid item xs={12} key={service}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="subtitle1" sx={{ textTransform: 'capitalize', mb: 1 }}>
                        {service}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={getUsagePercentage(service)}
                            color={getUsagePercentage(service) > 80 ? 'error' : 'primary'}
                          />
                        </Box>
                        <Typography variant="body2">
                          {usage.current_usage?.[service] || 0} / {usage.limits?.[service] || 'Unlimited'}
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                ))}

                {usage.last_sync_at && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Last synced: {new Date(usage.last_sync_at).toLocaleString()}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            )}
          </CardContent>
        </Card>
      </TabPanel>
    </Box>
  );
};

export default TwilioSettingsComponent;
